import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [admin, setAdmin] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAdmin(data);
      } else {
        setAdmin(false);
      }
    } catch {
      setAdmin(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    // Read body once as text, parse defensively — avoids "body stream already read" under StrictMode
    const raw = await res.text();
    let data = {};
    try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }
    if (!res.ok) {
      const err = new Error(formatDetail(data?.detail) || "Inloggen mislukt");
      err.status = res.status;
      throw err;
    }
    // If 2FA is enabled, do NOT set admin yet — caller must complete step 2 with verify2FA()
    if (data?.requires_2fa) {
      return { requires_2fa: true, pending_2fa_token: data.pending_2fa_token };
    }
    setAdmin(data);
    return data;
  }, []);

  const verify2FA = useCallback(async (pending_2fa_token, code) => {
    const res = await fetch(`${API}/auth/2fa/verify`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pending_2fa_token, code }),
    });
    const raw = await res.text();
    let data = {};
    try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }
    if (!res.ok) {
      const err = new Error(formatDetail(data?.detail) || "2FA-verificatie mislukt");
      err.status = res.status;
      throw err;
    }
    setAdmin(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) {
      // Network failure — proceed with local logout anyway
      console.warn("Logout request failed, clearing local state:", e);
    }
    setAdmin(false);
  }, []);

  return (
    <AuthContext.Provider value={{ admin, loading: admin === null, login, verify2FA, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

function formatDetail(d) {
  if (d == null) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}
